/**
 * purposes.ts — the ONE frontend source for metafield-purpose metadata
 * (BLU-536 / Phase 56.1 Plan 07).
 *
 * The purpose UNION and the variant-scope SET are not defined here — they
 * come from `@threadheads/shared` (`data/metafield-purposes.ts`), the single
 * source shared with the backend router, the backend types module, and the
 * DB CHECK constraints. This file adds the frontend-only presentation
 * metadata on top: labels, help copy, key placeholders, render order,
 * grouping, and the recommended-setup seeds.
 *
 * Every Record below is typed over the FULL `Purpose` union, so adding a
 * purpose to the shared tuple fails typecheck HERE until its label, help,
 * placeholder, and group are written. That is deliberate — it replaces the
 * three drifting per-file copies this module consolidated
 * (ProductDataLookupCard, MetafieldPicker, SkuTestSection).
 *
 * Microcopy rules: operator language, curly apostrophes (’), no em-dashes
 * (DESIGN.md §17 + flawless-typography).
 */

import {
  METAFIELD_PURPOSES,
  VARIANT_SCOPED_PURPOSES,
  type MetafieldPurpose,
} from '@threadheads/shared';

// ---------------------------------------------------------------------------
// Union + scope set (re-exported from the shared single source)
// ---------------------------------------------------------------------------

/** Frontend alias for the shared wire union. */
export type Purpose = MetafieldPurpose;

/**
 * Purposes whose metafield lives on the Shopify ProductVariant. This is the
 * SAME OBJECT as the shared `VARIANT_SCOPED_PURPOSES` set (identity, not a
 * mirror) — the lockstep test asserts `Object.is` equality so a local copy
 * can never be reintroduced silently.
 */
export const VARIANT_PURPOSES: ReadonlySet<Purpose> = VARIANT_SCOPED_PURPOSES;

// ---------------------------------------------------------------------------
// Core vs advanced split (Phase 56.1 IA decision — review-pinned)
// ---------------------------------------------------------------------------

/**
 * The three rows most operators need, rendered FLAT at the top of the card
 * (no group headings — their labels already say front/back/variant). Also
 * the only purposes seeded by "Use recommended setup".
 */
export const CORE_PURPOSES = [
  'design_id',
  'back_design_id',
  'design_variant',
] as const satisfies readonly Purpose[];

export type CorePurpose = (typeof CORE_PURPOSES)[number];

/**
 * The remaining purposes, rendered inside the "Advanced print mapping"
 * disclosure, grouped Front / Back / Shared. Order here is display order
 * WITHIN the disclosure (shape, variant, platen, platen variant, artwork
 * file, mockup image per side; then shared).
 */
export const ADVANCED_PURPOSES = [
  'front_design_shape',
  'front_design_variant',
  'front_design_platen',
  'front_design_platen_variant',
  'front_artwork',
  'front_mockup_preview',
  'back_design_shape',
  'back_design_variant',
  'back_design_platen',
  'back_design_platen_variant',
  'back_artwork',
  'back_mockup_preview',
  'fulfillment_type',
  'pretreat',
  'custom_print_sku',
  'print_technology',
] as const satisfies readonly Purpose[];

/**
 * Full render order: core 3 first, then the advanced purposes in their
 * grouped display order.
 */
export const ORDERED_PURPOSES = [
  ...CORE_PURPOSES,
  ...ADVANCED_PURPOSES,
] as const satisfies readonly Purpose[];

// Compile-time exhaustiveness: if a purpose is added to the shared tuple but
// not placed into CORE_PURPOSES or ADVANCED_PURPOSES above, the Exclude<>
// below becomes non-empty and this Record initializer fails typecheck.
type MissingFromOrdered = Exclude<Purpose, (typeof ORDERED_PURPOSES)[number]>;
const _orderedCoversEveryPurpose: Record<MissingFromOrdered, never> = {};
void _orderedCoversEveryPurpose;

// ---------------------------------------------------------------------------
// Presentation metadata (frontend-only; typed over the full union)
// ---------------------------------------------------------------------------

/** Row title shown next to the Active/Not set badge. */
export const PURPOSE_LABEL: Record<Purpose, string> = {
  design_id: 'Print file (front)',
  back_design_id: 'Back print file',
  design_variant: 'Design variant',
  front_design_shape: 'Front print shape',
  back_design_shape: 'Back print shape',
  front_design_variant: 'Front variant',
  back_design_variant: 'Back variant',
  front_design_platen: 'Front platen size',
  back_design_platen: 'Back platen size',
  front_design_platen_variant: 'Front platen variant',
  back_design_platen_variant: 'Back platen variant',
  fulfillment_type: 'Fulfilment type',
  pretreat: 'Pretreat',
  custom_print_sku: 'Custom print SKU',
  front_artwork: 'Front artwork file',
  back_artwork: 'Back artwork file',
  print_technology: 'Print technology',
  front_mockup_preview: 'Front mockup image',
  back_mockup_preview: 'Back mockup image',
};

/**
 * Short label used in toasts, aria-labels, and any other transient surface
 * where the row title doesn't read well mid-sentence.
 */
export const PURPOSE_SHORT_LABEL: Record<Purpose, string> = {
  design_id: 'front print',
  back_design_id: 'back print',
  design_variant: 'design variant',
  front_design_shape: 'front shape',
  back_design_shape: 'back shape',
  front_design_variant: 'front variant',
  back_design_variant: 'back variant',
  front_design_platen: 'front platen',
  back_design_platen: 'back platen',
  front_design_platen_variant: 'front platen variant',
  back_design_platen_variant: 'back platen variant',
  fulfillment_type: 'fulfilment type',
  pretreat: 'pretreat',
  custom_print_sku: 'custom print SKU',
  front_artwork: 'front artwork',
  back_artwork: 'back artwork',
  print_technology: 'print technology',
  front_mockup_preview: 'front mockup',
  back_mockup_preview: 'back mockup',
};

/**
 * Placeholder for the free-text Key input per purpose (MetafieldPicker shows
 * this when the dropdown is empty / errored / in custom mode). Matches the
 * conventional Shopify key name for each purpose.
 */
export const PURPOSE_KEY_PLACEHOLDER: Record<Purpose, string> = {
  design_id: 'design_id',
  back_design_id: 'back_design_id',
  design_variant: 'design_variant',
  front_design_shape: 'front_design_shape',
  back_design_shape: 'back_design_shape',
  front_design_variant: 'front_design_variant',
  back_design_variant: 'back_design_variant',
  front_design_platen: 'front_design_platen',
  back_design_platen: 'back_design_platen',
  front_design_platen_variant: 'front_design_platen_variant',
  back_design_platen_variant: 'back_design_platen_variant',
  fulfillment_type: 'fulfillment_type',
  pretreat: 'pretreat',
  custom_print_sku: 'custom_print_sku',
  front_artwork: 'front_artwork',
  back_artwork: 'back_artwork',
  print_technology: 'print_technology',
  front_mockup_preview: 'front_design_preview',
  back_mockup_preview: 'back_design_preview',
};

/** One-or-two-sentence operator help shown under each row title. */
export const PURPOSE_HELP: Record<Purpose, string> = {
  design_id: 'Identifies the front-side print file for each variant.',
  back_design_id: 'Identifies the back-side print file for dual-print products.',
  design_variant:
    'Tells us which colour variant a product is (Light, Dark, or Medium). Each variant in Shopify Admin needs its own value.',
  front_design_shape:
    'The front design’s shape (Rectangular, Square, Horizontal, or Circle). Picks the right print file size for the front of each garment.',
  back_design_shape:
    'The back design’s shape (Rectangular, Square, Horizontal, or Circle). Picks the right print file size for the back of each garment.',
  front_design_variant:
    'Colour variant for the front design (Light, Dark, or Medium). Overrides the shared design variant on the front side.',
  back_design_variant:
    'Colour variant for the back design (Light, Dark, or Medium). Overrides the shared design variant on the back side.',
  // ASCII x in the platen sizes deliberately (Plan 08 fix) — the backend's
  // platen storage format check rejects the × multiplication sign, so help
  // copy must not coach operators into entering it in Shopify.
  front_design_platen:
    'Platen size for the front print (10x12, 14x16, or 16x18). Stored for print setup; not used to find files.',
  back_design_platen:
    'Platen size for the back print (10x12, 14x16, or 16x18). Stored for print setup; not used to find files.',
  front_design_platen_variant:
    'Small or Large. Filled in by Print Run from your platen size rules when artwork is uploaded. Nobody types it.',
  back_design_platen_variant:
    'Small or Large. Filled in by Print Run from your platen size rules when artwork is uploaded. Nobody types it.',
  fulfillment_type:
    'Whether a product is printed on demand (pod), picked from stock (pap), or a blank. Mapped now so it’s ready; it doesn’t change behaviour yet.',
  pretreat:
    'Whether garments need pretreatment before printing. Mapped now so it’s ready; it doesn’t change behaviour yet.',
  custom_print_sku:
    'When a variant’s Shopify SKU doesn’t match our print-file format, point us at the Shopify variant metafield that holds the matching print SKU. The value must start with "CUST-".',
  front_artwork:
    'The print file for the front of this variant: either a bare filename (for example my-design-front.png) or a full https:// link to the PNG. Skipped for any variant that has a custom print SKU. Works alongside a front print file (design ID) mapping; link values are picked up by the same product sync.',
  back_artwork:
    'The print file for the back of this variant, as a bare filename or a full https:// link. Leave unset for front-only products.',
  print_technology:
    'DTG or DTF for the whole product. When mapped, Shopify is the authoritative source: a value overrides per-SKU settings in the IMS, and an empty value clears back to the location default.',
  front_mockup_preview:
    'The picture of the finished garment with the front design on it. Packers see this first when they check an order, ahead of the print file and the Shopify product photo. Point it at a Shopify variant field holding an image, or at one holding a full https:// link to an image. Shown on screen only; never sent to a printer.',
  back_mockup_preview:
    'The same picture for the back of the garment, on products printed both sides. Leave unset for front-only products. Shown on screen only; never sent to a printer.',
};

// ---------------------------------------------------------------------------
// Grouping (drives the Advanced disclosure's Front / Back / Shared sections)
// ---------------------------------------------------------------------------

export type PurposeGroup = 'front' | 'back' | 'shared';

export const PURPOSE_GROUP: Record<Purpose, PurposeGroup> = {
  design_id: 'front',
  back_design_id: 'back',
  design_variant: 'shared',
  front_design_shape: 'front',
  back_design_shape: 'back',
  front_design_variant: 'front',
  back_design_variant: 'back',
  front_design_platen: 'front',
  back_design_platen: 'back',
  front_design_platen_variant: 'front',
  back_design_platen_variant: 'back',
  fulfillment_type: 'shared',
  pretreat: 'shared',
  custom_print_sku: 'shared',
  front_artwork: 'front',
  back_artwork: 'back',
  print_technology: 'shared',
  front_mockup_preview: 'front',
  back_mockup_preview: 'back',
};

export interface AdvancedPurposeGroup {
  id: PurposeGroup;
  /** §13.4-style group label rendered above the rows. */
  label: string;
  purposes: ReadonlyArray<Purpose>;
}

/**
 * Advanced disclosure content, grouped + ordered. Derived from
 * ADVANCED_PURPOSES + PURPOSE_GROUP so the two can't disagree.
 */
export const ADVANCED_GROUPS: ReadonlyArray<AdvancedPurposeGroup> = (
  [
    { id: 'front', label: 'Front side' },
    { id: 'back', label: 'Back side' },
    { id: 'shared', label: 'Shared' },
  ] as const
).map(({ id, label }) => ({
  id,
  label,
  purposes: ADVANCED_PURPOSES.filter((purpose) => PURPOSE_GROUP[purpose] === id),
}));

// ---------------------------------------------------------------------------
// Recommended setup (CORE 3 ONLY — deliberate)
// ---------------------------------------------------------------------------

/**
 * Default Shopify namespace + key seeded by "Use recommended setup".
 *
 * ONLY the core 3 are seeded. The advanced purposes stay unseeded on
 * purpose: absent mappings are byte-identical to today's behaviour, while a
 * guessed namespace would create red Test bands on stores that never set
 * those metafields up (Phase 56.1 plan decision).
 */
export const RECOMMENDED_DEFAULTS: Record<CorePurpose, { namespace: string; key: string }> = {
  design_id: { namespace: 'my_fields', key: 'design_id' },
  back_design_id: { namespace: 'custom', key: 'back_design_id' },
  design_variant: { namespace: 'custom', key: 'design_variant' },
};

// Re-export the shared tuple for consumers (and the lockstep test) that need
// to iterate the canonical order rather than the display order.
export { METAFIELD_PURPOSES };
